/**
 * ============================================================================
 * CACHE CONTEXT - React Context for Global Cache Management
 * ============================================================================
 * 
 * Provides:
 * - Global cache invalidation methods
 * - Cache status tracking
 * - Centralized cache operations across components
 * 
 * ============================================================================
 */

import React, { createContext, useContext, useCallback, useState } from 'react';
import cacheManager from '../utils/cacheManager';

const CacheContext = createContext();

export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within CacheProvider');
  }
  return context;
};

export const CacheProvider = ({ children }) => {
  // Force re-render when cache is invalidated
  const [cacheVersion, setCacheVersion] = useState(0);

  /**
   * Invalidate all members-related cache
   */
  const invalidateMembers = useCallback(() => {
    console.log('🔄 [CacheContext] Invalidating members cache');
    cacheManager.invalidatePattern('members');
    cacheManager.invalidatePattern('all-users');
    setCacheVersion(v => v + 1);
  }, []);

  /**
   * Invalidate all tasks-related cache
   */
  const invalidateTasks = useCallback(() => {
    console.log('🔄 [CacheContext] Invalidating tasks cache');
    cacheManager.invalidatePattern('tasks');
    cacheManager.invalidatePattern('task-stats');
    setCacheVersion(v => v + 1);
  }, []);

  /**
   * Invalidate specific member profile cache
   */
  const invalidateMemberProfile = useCallback((memberId) => {
    console.log(`🔄 [CacheContext] Invalidating member profile: ${memberId}`);
    cacheManager.delete(`member-profile-${memberId}`);
    setCacheVersion(v => v + 1);
  }, []);

  /**
   * Invalidate all cache
   */
  const invalidateAll = useCallback(() => {
    console.log('🔄 [CacheContext] Invalidating all cache');
    cacheManager.clear();
    setCacheVersion(v => v + 1);
  }, []);

  /**
   * Get cache statistics
   */
  const getCacheStats = useCallback(() => {
    return cacheManager.getStats();
  }, []);

  const value = {
    invalidateMembers,
    invalidateTasks,
    invalidateMemberProfile,
    invalidateAll,
    getCacheStats,
    cacheVersion, // Can be used to trigger re-renders
    cacheManager, // Expose the manager for direct access if needed
  };

  return (
    <CacheContext.Provider value={value}>
      {children}
    </CacheContext.Provider>
  );
};

export default CacheContext;
